'use client';

import { type GetMarketRow, useGetMarketsField } from '@morfi/backend-sdk-v2/client';
import { BorderBeam, type BorderBeamPalette } from 'border-beam';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { Icon } from '@/components/ui/icon';
import { Agreement01IconSolid } from '@/components/ui/icon/glyphs';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { cn } from '@/lib/ui/utils';

type MarketType = GetMarketRow['marketType'] | undefined;

/**
 * Featured beam in the brand ramp. Stop geometry mirrors the package's `ocean`
 * preset; colours come from design tokens (the patched `border-beam` accepts any
 * CSS colour and alpha-mixes it via `color-mix`). `staticColors` keeps the hue
 * pinned to brand instead of cycling.
 */
const FEATURED_BEAM: BorderBeamPalette = {
  border: [
    { color: 'var(--brand-500)', pos: '33% -7.4%', size: '70px 40px' },
    { color: 'var(--brand-400)', pos: '12% -5%', size: '60px 35px' },
    { color: 'var(--brand-600)', pos: '2.1% 68.3%', size: '40px 70px' },
    { color: 'var(--brand-300)', pos: '2.1% 68.3%', size: '20px 35px' },
    { color: 'var(--brand-500)', pos: '74.4% 100%', size: '180px 32px' },
    { color: 'var(--brand-400)', pos: '55% 100%', size: '85px 26px' },
    { color: 'var(--brand-300)', pos: '93.9% 0%', size: '74px 32px' },
    { color: 'var(--brand-400)', pos: '100% 27.1%', size: '26px 42px' },
    { color: 'var(--brand-600)', pos: '100% 27.1%', size: '52px 48px' },
  ],
  spike: { primary: 'var(--brand-400)', secondary: 'var(--brand-300)' },
  spikeLt: { primary: 'var(--brand-600)', secondary: 'var(--brand-500)' },
};

interface IdentityBeamProps {
  marketType: MarketType;
  /** Outer radius in px so the glow clips to the same corner as the wrapped surface. */
  radius?: 12 | 20;
  className?: string | undefined;
  children: ReactNode;
}

/**
 * Animated inner-border beam for influencer and Featured markets — the same
 * `border-beam` glow as the AI creation entry box. Creators get the colourful
 * spectrum, Featured markets the Morfi ocean (brand blue) palette. Renders children untouched for ordinary
 * markets or while the feature gate is off/loading.
 */
export function IdentityBeam({
  marketType,
  radius = 20,
  className,
  children,
}: IdentityBeamProps): ReactNode {
  const enabled = useFeatureGate('creator_and_platform_markets') === true;
  if (!enabled || (marketType !== 'influencer' && marketType !== 'featured')) {
    return className ? <div className={className}>{children}</div> : children;
  }
  return (
    <BorderBeam
      size="pulse-inner"
      colorVariant={marketType === 'influencer' ? 'colorful' : FEATURED_BEAM}
      staticColors={marketType === 'featured'}
      theme="auto"
      borderRadius={radius}
      strength={1.4}
      brightness={1.5}
      data-testid="identity-beam"
      data-beam={marketType}
      {...(className !== undefined ? { className } : {})}
    >
      {children}
    </BorderBeam>
  );
}

/** Feed-cache variant: reads `marketType` for a market pubkey via the SDK field hook. */
export function MarketFeedIdentityBeam({
  pubkey,
  radius,
  className,
  children,
}: Omit<IdentityBeamProps, 'marketType'> & { pubkey: string }): ReactNode {
  const marketType = useGetMarketsField(pubkey, 'marketType');
  return (
    <IdentityBeam marketType={marketType} className={className} {...(radius ? { radius } : {})}>
      {children}
    </IdentityBeam>
  );
}

// `icon` carries an explicit size AND text class: command items (search) restyle any
// svg lacking `size-`/`text-` classes, which would grey and enlarge the glyph.
const MARK_SIZE: Record<'sm' | 'md', { chip: string; icon: string; image: number }> = {
  md: { chip: 'size-5 -right-1 -bottom-1', icon: 'size-3', image: 20 },
  sm: { chip: 'size-4 -right-0.5 -bottom-0.5', icon: 'size-2.5', image: 16 },
};

/**
 * Origin mark pinned to the bottom-right of a market image: a handshake for
 * influencer markets, the Morfi logomark for Featured markets. Mirrors the
 * geometry of the market card's location badge.
 */
export function OriginMark({
  marketType,
  size = 'md',
}: {
  marketType: MarketType;
  size?: 'sm' | 'md';
}): ReactNode {
  const enabled = useFeatureGate('creator_and_platform_markets') === true;
  if (!enabled || marketType === undefined || marketType === 'ordinary') return null;
  const geometry = MARK_SIZE[size];
  return (
    <span
      aria-hidden="true"
      data-testid="market-origin-mark"
      data-origin={marketType}
      className={cn(
        'absolute grid place-items-center overflow-hidden rounded-full border border-border-primary bg-background-primary',
        marketType === 'influencer' && 'text-product-partner-content',
        geometry.chip,
      )}
    >
      {marketType === 'featured' ? (
        <OptimizedImage
          src="/icons/morfi-logomark.svg"
          variant="avatar"
          // ALLOW-NO-PREVIEW: bundled brand mark is identity chrome, not user media.
          preview={false}
          // ALLOW-NSFW-FALSE: trusted bundled Morfi logo, not uploaded media.
          nsfw={false}
          size={geometry.image}
          rounded="rounded-full"
          alt=""
        />
      ) : (
        <Icon
          icon={Agreement01IconSolid}
          className={cn(geometry.icon, 'text-product-partner-content')}
        />
      )}
    </span>
  );
}

/** Wraps a market thumbnail so the origin mark can sit on its corner. */
export function ThumbWithOrigin({
  marketType,
  size,
  className,
  children,
}: {
  marketType: MarketType;
  size?: 'sm' | 'md';
  className?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <span className={cn('relative inline-flex flex-none', className)}>
      {children}
      <OriginMark marketType={marketType} {...(size !== undefined ? { size } : {})} />
    </span>
  );
}

/** Feed-cache variants: read `marketType` for a market pubkey via the SDK field hook. */
export function MarketFeedOriginMark({ pubkey }: { pubkey: string }): ReactNode {
  const marketType = useGetMarketsField(pubkey, 'marketType');
  return <OriginMark marketType={marketType} />;
}

/** 12px Morfi logomark used as the leading glyph of every Featured pill. */
export function FeaturedLogomark(): ReactNode {
  return (
    <OptimizedImage
      src="/icons/morfi-logomark.svg"
      variant="avatar"
      // ALLOW-NO-PREVIEW: bundled brand mark is identity chrome, not user media.
      preview={false}
      // ALLOW-NSFW-FALSE: trusted bundled Morfi logo, not uploaded media.
      nsfw={false}
      size={12}
      rounded="rounded-full"
      alt=""
    />
  );
}
